/**
 * Frontend API Types
 *
 * These types mirror the backend shared types but use serializable formats
 * (strings instead of Firebase Timestamp).
 */

// ============================================
// Workflow Stage Types
// ============================================

export type WorkflowStage =
  | 'ORDER_RECEIVED'
  | 'DIGITAL_MOCKUP'
  | 'AWAITING_APPROVAL'
  | 'APPROVED'
  | 'DIGITIZED_MOLD_SENT'
  | 'DIGITIZED_MOLD_RECEIVED'
  | 'IN_PRODUCTION'
  | 'IN_QA'
  | 'READY_TO_SHIP'
  | 'SHIPPED'
  | 'COMPLETE'
  | 'ARCHIVED';

/**
 * Product type determines which workflow an order follows.
 * - embroidery: Full workflow including digitization stages
 * - patch: Simplified workflow skipping digitization (artwork → proof directly)
 */
export type ProductType = 'embroidery' | 'patch' | 'both' | 'undetermined';

/**
 * Display labels for product types
 */
export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  embroidery: 'Embroidery',
  patch: 'Patch',
  both: 'Embroidery & Patch',
  undetermined: 'Undetermined',
};

/**
 * Workflow stages for each product type
 */
export const WORKFLOW_STAGES_BY_TYPE: Record<ProductType, WorkflowStage[]> = {
  embroidery: [
    'ORDER_RECEIVED',
    'DIGITAL_MOCKUP',
    'AWAITING_APPROVAL',
    'APPROVED',
    'DIGITIZED_MOLD_SENT',
    'DIGITIZED_MOLD_RECEIVED',
    'IN_PRODUCTION',
    'IN_QA',
    'READY_TO_SHIP',
    'SHIPPED',
    'COMPLETE',
    'ARCHIVED',
  ],
  patch: [
    'ORDER_RECEIVED',
    'DIGITAL_MOCKUP',
    'AWAITING_APPROVAL',
    'APPROVED',
    'DIGITIZED_MOLD_SENT',
    'DIGITIZED_MOLD_RECEIVED',
    'IN_PRODUCTION',
    'IN_QA',
    'READY_TO_SHIP',
    'SHIPPED',
    'COMPLETE',
    'ARCHIVED',
  ],
  both: [
    'ORDER_RECEIVED',
    'DIGITAL_MOCKUP',
    'AWAITING_APPROVAL',
    'APPROVED',
    'DIGITIZED_MOLD_SENT',
    'DIGITIZED_MOLD_RECEIVED',
    'IN_PRODUCTION',
    'IN_QA',
    'READY_TO_SHIP',
    'SHIPPED',
    'COMPLETE',
    'ARCHIVED',
  ],
  undetermined: [
    'ORDER_RECEIVED',
    'DIGITAL_MOCKUP',
    'AWAITING_APPROVAL',
    'APPROVED',
    'DIGITIZED_MOLD_SENT',
    'DIGITIZED_MOLD_RECEIVED',
    'IN_PRODUCTION',
    'IN_QA',
    'READY_TO_SHIP',
    'SHIPPED',
    'COMPLETE',
    'ARCHIVED',
  ],
};

export type FileType =
  | 'customer_artwork'
  | 'digitization_source'
  | 'digitization_result'
  | 'proof'
  | 'mockup'
  | 'production_file'
  | 'customer_reply'
  | 'other';

// Stage display names for UI
export const STAGE_LABELS: Record<WorkflowStage, string> = {
  ORDER_RECEIVED: 'Order Received',
  DIGITAL_MOCKUP: 'Digital Mockup',
  AWAITING_APPROVAL: 'Awaiting Approval',
  APPROVED: 'Approved',
  DIGITIZED_MOLD_SENT: 'Digitized/Mold Sent',
  DIGITIZED_MOLD_RECEIVED: 'Digitized/Mold Received',
  IN_PRODUCTION: 'In Production',
  IN_QA: 'In QA',
  READY_TO_SHIP: 'Ready to Ship',
  SHIPPED: 'Shipped',
  COMPLETE: 'Complete',
  ARCHIVED: 'Archived',
};

// Stage badge tones for UI
export const STAGE_TONES: Record<WorkflowStage, 'info' | 'success' | 'warning' | 'attention' | 'critical' | undefined> = {
  ORDER_RECEIVED: 'info',
  DIGITAL_MOCKUP: 'info',
  AWAITING_APPROVAL: 'warning',
  APPROVED: 'success',
  DIGITIZED_MOLD_SENT: 'attention',
  DIGITIZED_MOLD_RECEIVED: 'success',
  IN_PRODUCTION: 'info',
  IN_QA: 'attention',
  READY_TO_SHIP: 'success',
  SHIPPED: 'success',
  COMPLETE: 'success',
  ARCHIVED: undefined,
};

// ============================================
// Tracked Order Types
// ============================================

export interface LineItemProperty {
  name: string;
  value: string;
}

export interface TrackedLineItem {
  shopifyLineItemId: string;
  shopifyProductId: string;
  shopifyVariantId: string;
  productTitle: string;
  variantTitle?: string;
  sku?: string;
  quantity: number;
  properties: LineItemProperty[];
}

export interface StageTransition {
  id: string;
  from: WorkflowStage | null;
  to: WorkflowStage;
  timestamp: string;
  performedBy: string;
  performedByName?: string;
  performedByType: 'staff' | 'customer' | 'system';
  notes?: string;
}

/**
 * Data captured when an embroidery worker completes production on an order.
 */
export interface ProductionCompletionData {
  threadColors: string[];
  stitchCount: number;
  measurements: {
    width: number;
    height: number;
    unit: 'in' | 'mm';
  };
  completedBy: string;
  completedAt: string;
  notes?: string;
  defects?: string[];
}

/**
 * A hat item assigned to a specific mockup/production assignment.
 */
export interface HatAssignment {
  sourceLineItemId?: string;
  color: string;
  size: string;
  quantity: number;
  isManualOverride?: boolean;
}

/**
 * Maps a mockup/proof to specific hats. Each assignment is completed independently.
 */
export interface ProductionAssignment {
  id: string;
  mockupFileId: string;
  digitizationFileId?: string;
  label?: string;
  hatItems: HatAssignment[];
  status: 'pending' | 'completed';
  productionData?: ProductionCompletionData;
}

export interface TrackedOrder {
  id: string;
  shopifyOrderId: string;
  shopifyOrderNumber: string;
  shopifyOrderName: string;
  customerEmail: string;
  customerName: string;
  customerPhone?: string;
  shopifyNote?: string;
  lineItems: TrackedLineItem[];
  productType: ProductType;
  currentStage: WorkflowStage;
  stageHistory: StageTransition[];
  shopifyCreatedAt: string;
  createdAt: string;
  updatedAt: string;
  fileCount: number;
  approvalCount: number;
  productionData?: ProductionCompletionData;
  productionAssignments?: ProductionAssignment[];

  // Size selection (post-approval)
  sizeSelectionStatus?: 'pending' | 'completed';
  sizeSelectionCompletedAt?: string;
  sizeSelectionProductIds?: string[];

  // Revenue
  totalPrice?: string;
  currency?: string;

  // Multi-store
  shopDomain?: string;

  // Draft order support
  isDraft?: boolean;
  shopifyDraftOrderId?: string;
  shopifyDraftOrderName?: string;
  draftOrderStatus?: string;
  convertedFromDraftDocId?: string;
  convertedAt?: string;
}

// ============================================
// Approval Types
// ============================================

export interface MockupApproval {
  fileId: string;
  status: 'pending' | 'approved' | 'rejected';
  rejectionReason?: string;
  respondedAt?: string;
}

export interface Approval {
  id: string;
  orderId: string;
  orderNumber: string;
  proofFileId: string;
  proofFileUrl: string;
  proofFileIds?: string[];
  proofFileUrls?: string[];
  proofNotes?: string;
  approvalToken: string;
  tokenExpiresAt: string;
  sentAt: string;
  sentTo: string;
  sentBy: string;
  sendgridMessageId?: string;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  respondedAt?: string;
  rejectionReason?: string;
  mockupApprovals?: MockupApproval[];
  version: number;
  previousApprovalId?: string;
}

// ============================================
// File Types
// ============================================

export interface OrderFile {
  id: string;
  orderId: string;
  type: FileType;
  storagePath: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedBy: string;
  uploadedByType: 'staff' | 'system';
  source: 'manual_upload' | 'gmail_import' | 'upload' | 'order_note_url';
  // External URL for files from order notes (Logo_URL)
  externalUrl?: string;
  // Signed download URL generated by backend for Cloud Storage files
  downloadUrl?: string;
  gmailMessageId?: string;
  gmailThreadId?: string;
  gmailFrom?: string;
  gmailSubject?: string;
  gmailReceivedAt?: string;
  isActive: boolean;
  notes?: string;
  // Production prep
  selectedForProduction?: boolean;
  // Embroidery file metadata (auto-extracted from DST/PES/JEF etc.)
  embroideryMetadata?: {
    totalStitches: number;
    colorChanges: number;
    widthMm: number;
    heightMm: number;
    widthIn: number;
    heightIn: number;
    format: string;
    processedAt: string;
    error?: string;
  };
}

// File type display names
export const FILE_TYPE_LABELS: Record<FileType, string> = {
  customer_artwork: 'Customer Artwork',
  digitization_source: 'Digitization Source',
  digitization_result: 'Digitization Result',
  proof: 'Proof',
  mockup: 'Mockup',
  production_file: 'Production File',
  customer_reply: 'Customer Reply',
  other: 'Other',
};

// ============================================
// Settings Types
// ============================================

export interface TrackedProduct {
  id?: string;
  shopifyProductId: string;
  shopifyProductTitle: string;
  shopifyProductHandle: string;
  productType: ProductType;
  priceRange?: { min: string; max: string; currencyCode: string };
  addedAt?: string;
  addedBy?: string;
}

export interface GmailConnectionStatus {
  isConnected: boolean;
  email?: string;
  connectedAt?: string;
  watchExpiration?: string;
}

export interface EmailSenderPattern {
  id: string;
  email: string;
  name: string;
  isActive: boolean;
  addedAt?: string;
}

// ============================================
// Communication Types
// ============================================

export interface InboundEmail {
  id: string;
  orderId: string | null;
  from: string;
  fromName: string;
  subject: string;
  textBody: string;
  attachmentCount: number;
  attachmentFileIds: string[];
  status: 'matched' | 'unmatched' | 'duplicate';
  createdAt: string;
}

export interface EmailLog {
  id?: string;
  type: string;
  direction?: string;
  orderId: string;
  from?: string;
  to?: string;
  subject?: string;
  sentAt: string;
  status?: string;
  messageId?: string;
  newStatus?: string;
  approvalId?: string;
  // SendGrid tracking
  delivered?: boolean;
  deliveredAt?: string;
  opened?: boolean;
  openedAt?: string;
  openCount?: number;
  clicked?: boolean;
  clickedAt?: string;
  clickCount?: number;
  bounced?: boolean;
  bouncedAt?: string;
  bounceReason?: string;
}

export interface GmailAccount {
  id: string;
  email: string;
  label: string;
  isActive: boolean;
  connectedAt?: string;
  watchExpiration?: string;
}

export interface GmailMessageRecord {
  id: string;
  accountId: string;
  accountEmail: string;
  threadId: string;
  from: string;
  to: string;
  subject: string;
  bodySnippet: string;
  date: string;
  direction: 'inbound' | 'outbound';
  orderId: string | null;
  customerEmail: string | null;
  hasAttachments: boolean;
}

// ============================================
// Follow-Up Notification Types
// ============================================

export type FollowUpChannel = 'email' | 'sms';
export type FollowUpAudience = 'customer' | 'staff';
export type FollowUpTiming = 'immediate' | 'reminder';

export interface FollowUpRule {
  id: string;
  stage: WorkflowStage;
  timing: FollowUpTiming;
  audience: FollowUpAudience;
  channel: FollowUpChannel;
  enabled: boolean;
  delayHours?: number;
  repeatIntervalHours?: number;
  maxReminders?: number;
  subjectTemplate: string;
  bodyTemplate: string;
  description?: string;
}

export interface FollowUpConfig {
  enabled: boolean;
  staffEmail: string;
  staffPhone: string;
  rules: FollowUpRule[];
  updatedAt: string;
  updatedBy: string;
}

export interface FollowUpSent {
  id: string;
  ruleId: string;
  orderId: string;
  orderName: string;
  stage: WorkflowStage;
  timing: FollowUpTiming;
  audience: FollowUpAudience;
  channel: FollowUpChannel;
  recipientEmail?: string;
  recipientPhone?: string;
  subject?: string;
  bodyPreview: string;
  sentAt: string;
  reminderCount?: number;
  success: boolean;
  error?: string;
}

// ============================================
// API Request/Response Types
// ============================================

export interface OrderListQuery {
  stage?: WorkflowStage;
  excludeStage?: WorkflowStage;
  search?: string;
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  filter?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

export interface OrderListResponse {
  orders: TrackedOrder[];
  total: number;
  page: number;
  limit: number;
  stageCounts: Record<WorkflowStage, number>;
  needsInvoiceCount?: number;
}

export interface OrderDetailResponse {
  order: TrackedOrder;
  files: OrderFile[];
  approvals: Approval[];
  inboundEmails: InboundEmail[];
  emailLogs: EmailLog[];
  gmailMessages: GmailMessageRecord[];
}

export interface TransitionRequest {
  toStage: WorkflowStage;
  notes?: string;
}

export interface TransitionResponse {
  success: boolean;
  order: TrackedOrder;
  transition: StageTransition;
}

export interface FileUploadRequest {
  filename: string;
  mimeType: string;
  fileType: FileType;
}

export interface FileUploadResponse {
  uploadUrl: string;
  fileId: string;
}

export interface ApprovalRequest {
  proofFileId?: string;
  proofFileIds?: string[];
  notes?: string;
  productType?: ProductType;
  logoMappings?: Record<string, string>; // mockupFileId -> logoFileId
}

export interface ApprovalResponse {
  approvalId: string;
  approvalUrl: string;
}

export interface ApprovalPreviewResponse {
  html: string;
  subject: string;
  to: string;
  customerName: string;
  mockupImageUrl: string;
  mockupImageUrls?: string[];
}

export interface DashboardStats {
  stageCounts: Record<WorkflowStage, number>;
  mockupsReceivedCount: number;
  mockupsReceivedAllTime: number;
  medianApprovalHours: number | null;
  needsInvoiceCount: number;
  revenue: {
    total: number;
    currency: string;
    byCurrency: Record<string, number>;
  };
  unpaidRevenue: {
    total: number;
    currency: string;
    byCurrency: Record<string, number>;
    orderCount: number;
  };
}

// ============================================
// Reports Types
// ============================================

export interface DailyMockupEntry {
  date: string;
  app: number;
  jotform: number;
  total: number;
}

export interface DailyMockupsReport {
  entries: DailyMockupEntry[];
  countries: string[];
}

// ============================================
// Product Search Types
// ============================================

export interface ShopifyProduct {
  id: string;
  title: string;
  handle: string;
  vendor?: string;
  productType?: string;
  featuredImage?: {
    url: string;
    altText?: string;
  };
  priceRangeV2?: {
    minVariantPrice: { amount: string; currencyCode: string };
    maxVariantPrice: { amount: string; currencyCode: string };
  };
  variants?: {
    edges: Array<{
      node: {
        id: string;
        title: string;
        sku?: string;
      };
    }>;
  };
}

export interface ProductSearchResponse {
  products: ShopifyProduct[];
  hasNextPage: boolean;
  endCursor?: string;
}

// ============================================
// API Error Type
// ============================================

export interface ApiError {
  error: string;
  message?: string;
}

// Enhanced transition error with context
export interface TransitionErrorResponse extends ApiError {
  currentStage?: WorkflowStage;
  currentStageLabel?: string;
  requestedStage?: WorkflowStage;
  requestedStageLabel?: string;
  availableTransitions?: { stage: WorkflowStage; label: string }[];
  missingPrerequisites?: string[];
}
